import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ApiError, queryKeys, unwrap } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from './use-auth';
import type { AuthTokens, UserProfile } from './session';

export const identityKeys = queryKeys.module('identity').entity('me');

/** Turns any thrown value into something safe to render. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isValidation && error.fieldErrors) {
      return Object.values(error.fieldErrors).flat().join(' ');
    }

    return error.message;
  }

  return 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';
}

/**
 * Everything cached before a sign-in was fetched ANONYMOUSLY.
 *
 * That is not a theoretical staleness: a leaderboard's `isYou` flag is computed
 * server-side from the caller's identity, so the page that says "sign in to see
 * yourself in the standings" would keep not highlighting the row for the whole
 * stale time. Since the dialog never remounts the page behind it, nothing else
 * would ever force the refetch.
 */
function useSessionStart() {
  const { setSession } = useAuth();
  const queryClient = useQueryClient();

  return (tokens: AuthTokens) => {
    setSession(tokens.user, tokens.accessToken);
    void queryClient.invalidateQueries();
  };
}

export function useRegister() {
  const onSession = useSessionStart();

  return useMutation({
    mutationFn: (body: { userName: string; email: string; password: string }) =>
      unwrap<AuthTokens>(api.POST('/api/v1/identity/auth/register', { body })),
    onSuccess: onSession,
  });
}

export function useLogin() {
  const onSession = useSessionStart();

  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      unwrap<AuthTokens>(api.POST('/api/v1/identity/auth/login', { body })),
    onSuccess: onSession,
  });
}

export function useVerifyEmail() {
  const { updateUser } = useAuth();

  return useMutation({
    mutationFn: (token: string) =>
      unwrap<UserProfile>(api.POST('/api/v1/identity/auth/verify-email', { body: { token } })),
    onSuccess: (user) => updateUser(user),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      unwrap(api.POST('/api/v1/identity/auth/resend-verification', { body: { email } })),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      unwrap(api.POST('/api/v1/identity/auth/forgot-password', { body: { email } })),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; newPassword: string }) =>
      unwrap(api.POST('/api/v1/identity/auth/reset-password', { body })),
  });
}

export function useConfirmEmailChange() {
  const { updateUser, isAuthenticated } = useAuth();

  return useMutation({
    mutationFn: (token: string) =>
      unwrap<UserProfile>(
        api.POST('/api/v1/identity/auth/confirm-email-change', { body: { token } }),
      ),
    onSuccess: (user) => {
      // Only when this browser IS that user. The link is often opened in
      // whichever browser the new inbox is signed into, and writing someone
      // else's profile into an anonymous session would be a small disaster.
      if (isAuthenticated) {
        updateUser(user);
      }
    },
  });
}

export function useSignOut() {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: signOut,
    onSettled: () => {
      // Every cached query was scoped to the user who just left; keeping any of
      // it would show the next person their data.
      queryClient.clear();

      // And then LEAVE. Sign-out is normally pressed on the profile, which is
      // behind RequireAuth — staying would drop straight into "you must sign in"
      // and pop the dialog back open, which reads as either a broken log-out or
      // a site demanding you log back in.
      void navigate('/', { replace: true });
    },
  });
}
